// Auto-genererer SEO-felter (slug, title, description) for biler ved sync.
// Bruges af Bilinfo-syncen og af backfill-migrationer. Ren funktion — ingen
// server-only imports, så den kan unit-testes og køres isomorf.
//
// Format-regler (Google best practice):
//  - title: 50–60 tegn, brand-model-variant-year + pris + brand-suffix
//  - description: 140–160 tegn, beriget med specs hvis Bilinfo-comment er tom
//  - slug: kebab-case ASCII, garanteret unik via mileage-suffix
//
// Strategi: ALTID overskriv ved sync (admin's manuelle ændringer går tabt).

export interface CarSeoInput {
  brand: string;
  model: string;
  variant: string | null;
  year: number;
  mileage: number;
  price: number;
  fuel_type?: string | null;
  transmission?: string | null;
  horsepower?: number | null;
  description?: string | null;
  is_leasing?: boolean;
  leasing_price?: number | null;
}

export interface CarSeoOutput {
  seo_slug: string;
  seo_title: string;
  seo_description: string;
}

/** Convert an arbitrary string to URL-safe kebab-case (ASCII only). */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[æ]/g, "ae")
    .replace(/[ø]/g, "oe")
    .replace(/[å]/g, "aa")
    .replace(/[^\w\s-]/g, " ") // remove non-word chars
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const FUEL_LABELS: Record<string, string> = {
  petrol: "Benzin",
  benzin: "Benzin",
  diesel: "Diesel",
  electric: "El",
  el: "El",
  hybrid: "Hybrid",
  plugin_hybrid: "Plugin Hybrid",
};

function fuelLabel(fuel?: string | null): string | null {
  if (!fuel) return null;
  return FUEL_LABELS[fuel.toLowerCase()] ?? fuel;
}

function transmissionLabel(t?: string | null): string | null {
  if (!t) return null;
  const lower = t.toLowerCase();
  if (lower.startsWith("auto")) return "Automatik";
  if (lower.startsWith("man")) return "Manuel";
  return t;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("da-DK").format(n);
}

/** Truncate to max chars on word boundary; ends with "…" when shortened. */
function clamp(text: string, max: number): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const trimmed = clean.slice(0, max - 1);
  const lastSpace = trimmed.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? trimmed.slice(0, lastSpace) : trimmed) + "…";
}

/**
 * Build the SEO slug. Format: brand-model-variant-year-mileage
 * Mileage suffix gives near-unique URLs even for identical brand/model/year.
 * Example: bmw-x5-3-0-d-xdrive-2021-85000km
 */
export function buildCarSlug(car: Pick<CarSeoInput, "brand" | "model" | "variant" | "year" | "mileage">): string {
  const parts = [
    car.brand,
    car.model,
    car.variant ?? "",
    String(car.year),
    `${car.mileage}km`,
  ].filter(Boolean);
  return slugify(parts.join(" "));
}

/**
 * Build SEO title. Aim for ~55–60 chars (Google truncates ~60).
 * Format: "{Brand} {Model} {Variant} {Year} – {Pris} kr · AutoXChange"
 * Falls back gracefully when too long by dropping variant.
 */
export function buildCarSeoTitle(car: CarSeoInput): string {
  const name = [car.brand, car.model, car.variant].filter(Boolean).join(" ");
  const priceLabel = car.is_leasing && car.leasing_price
    ? `Leasing ${formatNumber(car.leasing_price)} kr/md`
    : `${formatNumber(car.price)} kr`;
  const suffix = " | AutoXChange";

  const full = `${name} ${car.year} – ${priceLabel}${suffix}`;
  if (full.length <= 60) return full;

  // Drop variant if too long
  const shortName = `${car.brand} ${car.model}`;
  const compact = `${shortName} ${car.year} – ${priceLabel}${suffix}`;
  return compact.length <= 60 ? compact : clamp(compact, 60);
}

/**
 * Build SEO meta description. Aim for ~140–160 chars.
 * Strategy:
 *  - If Bilinfo-comment is meaningful (>40 chars), clamp it
 *  - Otherwise build from specs (km, brændstof, gear, HK, pris)
 */
export function buildCarSeoDescription(car: CarSeoInput): string {
  const name = [car.brand, car.model, car.variant].filter(Boolean).join(" ");
  const desc = (car.description ?? "").trim();

  // Use Bilinfo description if it's substantial
  if (desc.length >= 40) {
    const prefix = `${name} ${car.year}. `;
    const combined = prefix + desc;
    return clamp(combined, 158);
  }

  // Build spec-based description
  const specs: string[] = [];
  specs.push(`${formatNumber(car.mileage)} km`);
  const fuel = fuelLabel(car.fuel_type);
  if (fuel) specs.push(fuel);
  const trans = transmissionLabel(car.transmission);
  if (trans) specs.push(trans);
  if (car.horsepower) specs.push(`${car.horsepower} HK`);

  const priceLabel = car.is_leasing && car.leasing_price
    ? `Leasing fra ${formatNumber(car.leasing_price)} kr/md`
    : `Pris ${formatNumber(car.price)} kr`;

  const built = `${name} ${car.year} med ${specs.join(", ")}. ${priceLabel}. Se billeder, udstyr og book prøvetur hos AutoXChange i Børkop.`;
  return clamp(built, 158);
}

/** Build all three SEO fields in one call. */
export function buildCarSeo(car: CarSeoInput): CarSeoOutput {
  return {
    seo_slug: buildCarSlug(car),
    seo_title: buildCarSeoTitle(car),
    seo_description: buildCarSeoDescription(car),
  };
}
